import { useState, useEffect, useCallback } from 'react';
import { X, Link2, Copy, Trash2, Plus, RefreshCw, Play, Eye, EyeOff, CheckCircle2, AlertCircle, ExternalLink } from 'lucide-react';
import { API_URL } from '../config';

interface Webhook {
  id: string;
  flow_id: string;
  flow_name: string;
  webhook_url: string;
  webhook_path: string;
  description?: string;
  auth_type: string;
  enabled: boolean;
  created_at: string;
  call_count: number;
  last_called_at?: string;
}

interface WebhookCall {
  id: string;
  webhook_id: string;
  flow_name: string;
  method: string;
  payload: any;
  started_at: string;
  finished_at?: string;
  status: string;
  duration_ms?: number;
  error?: string;
}

interface WebhookPanelProps {
  isOpen: boolean;
  onClose: () => void;
  currentFlowId?: string;
  currentFlowName?: string;
  currentFlowDefinition?: any;
}

export default function WebhookPanel({
  isOpen,
  onClose,
  currentFlowId,
  currentFlowName,
  currentFlowDefinition,
}: WebhookPanelProps) {
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [calls, setCalls] = useState<WebhookCall[]>([]);
  const [activeTab, setActiveTab] = useState<'webhooks' | 'calls' | 'create'>('webhooks');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  
  // Form state
  const [description, setDescription] = useState('');
  const [authType, setAuthType] = useState<'none' | 'api_key' | 'signature'>('none');
  const [apiKey, setApiKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);

  const loadWebhooks = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/webhook/list`);
      if (res.ok) {
        const data = await res.json();
        setWebhooks(data);
      }
    } catch (e) {
      console.error('Error loading webhooks:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCalls = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/webhook/calls?limit=30`);
      if (res.ok) {
        const data = await res.json();
        setCalls(data);
      }
    } catch (e) {
      console.error('Error loading calls:', e);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadWebhooks();
      loadCalls();
    }
  }, [isOpen, loadWebhooks, loadCalls]);

  const createWebhook = async () => {
    if (!currentFlowId || !currentFlowName || !currentFlowDefinition) {
      setError('Nessun flow selezionato. Apri prima un flow.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const body: any = {
        flow_id: currentFlowId,
        flow_name: currentFlowName,
        flow_definition: currentFlowDefinition,
        description: description || undefined,
        auth_type: authType,
        enabled: true,
      };

      if (authType === 'api_key' && apiKey) {
        body.api_key = apiKey;
      }
      if (authType === 'signature' && secretKey) {
        body.secret_key = secretKey;
      }

      const res = await fetch(`${API_URL}/webhook/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        setActiveTab('webhooks');
        loadWebhooks();
        // Reset form
        setDescription('');
        setAuthType('none');
        setApiKey('');
        setSecretKey('');
      } else {
        const data = await res.json();
        setError(data.detail || 'Errore creazione webhook');
      }
    } catch (e) {
      setError('Errore di rete');
    } finally {
      setLoading(false);
    }
  };

  const toggleWebhook = async (id: string, enabled: boolean) => {
    try {
      await fetch(`${API_URL}/webhook/${id}/toggle?enabled=${enabled}`, {
        method: 'POST',
      });
      loadWebhooks();
    } catch (e) {
      console.error('Error toggling webhook:', e);
    }
  };

  const deleteWebhook = async (id: string) => {
    if (!confirm('Sei sicuro di voler eliminare questo webhook?')) return;
    
    try {
      await fetch(`${API_URL}/webhook/${id}`, {
        method: 'DELETE',
      });
      loadWebhooks();
    } catch (e) {
      console.error('Error deleting webhook:', e);
    }
  };

  const testWebhook = async (id: string) => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/webhook/test/${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ test: true, timestamp: new Date().toISOString() }),
      });
      const data = await res.json();
      
      if (data.success) {
        alert(`✅ Test riuscito!\nDurata: ${data.duration_ms}ms\nOutput: ${data.output || 'nessuno'}`);
      } else {
        alert(`❌ Test fallito: ${data.error}`);
      }
      
      loadCalls();
    } catch (e) {
      alert('Errore nel test');
    } finally {
      setLoading(false);
    }
  };

  const copyWebhookUrl = (webhook: Webhook) => {
    const fullUrl = `${window.location.origin.replace('studio.', 'flow.')}${webhook.webhook_url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedId(webhook.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatDate = (iso?: string) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('it-IT');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'text-green-600 bg-green-100';
      case 'error': return 'text-red-600 bg-red-100';
      case 'running': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-[900px] max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-500 to-purple-500 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 text-white">
            <Link2 className="w-6 h-6" />
            <h2 className="text-xl font-bold">Webhooks (n8n-style)</h2>
          </div>
          <button onClick={onClose} className="text-white hover:bg-white/20 p-2 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('webhooks')}
            className={`px-6 py-3 font-medium ${activeTab === 'webhooks' ? 'text-indigo-600 border-b-2 border-indigo-500' : 'text-gray-500'}`}
          >
            🔗 Webhooks ({webhooks.length})
          </button>
          <button
            onClick={() => setActiveTab('calls')}
            className={`px-6 py-3 font-medium ${activeTab === 'calls' ? 'text-indigo-600 border-b-2 border-indigo-500' : 'text-gray-500'}`}
          >
            📊 Chiamate ({calls.length})
          </button>
          <button
            onClick={() => setActiveTab('create')}
            className={`px-6 py-3 font-medium ${activeTab === 'create' ? 'text-indigo-600 border-b-2 border-indigo-500' : 'text-gray-500'}`}
          >
            <Plus className="w-4 h-4 inline mr-1" />
            Nuovo Webhook
          </button>
          <div className="flex-1" />
          <button onClick={() => { loadWebhooks(); loadCalls(); }} className="px-4 text-gray-500 hover:text-indigo-600">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}

          {/* Webhooks Tab */}
          {activeTab === 'webhooks' && (
            <div className="space-y-4">
              {webhooks.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Link2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nessun webhook configurato</p>
                  <button 
                    onClick={() => setActiveTab('create')}
                    className="mt-4 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600"
                  >
                    Crea il primo webhook
                  </button>
                </div>
              ) : (
                webhooks.map(webhook => (
                  <div key={webhook.id} className="border rounded-xl p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-bold text-lg">{webhook.flow_name}</h3>
                        {webhook.description && (
                          <p className="text-sm text-gray-600">{webhook.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono text-indigo-600">
                            {webhook.webhook_url}
                          </code>
                          <button
                            onClick={() => copyWebhookUrl(webhook)}
                            className="p-1 hover:bg-gray-100 rounded"
                            title="Copia URL"
                          >
                            {copiedId === webhook.id ? (
                              <CheckCircle2 className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-4 h-4 text-gray-400" />
                            )}
                          </button>
                          <a
                            href={`${API_URL}${webhook.webhook_url}?sync=true`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1 hover:bg-gray-100 rounded"
                            title="Apri in nuova tab"
                          >
                            <ExternalLink className="w-4 h-4 text-gray-400" />
                          </a>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => testWebhook(webhook.id)}
                          className="p-2 rounded-lg bg-indigo-100 text-indigo-600 hover:bg-indigo-200"
                          title="Testa webhook"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => toggleWebhook(webhook.id, !webhook.enabled)}
                          className={`p-2 rounded-lg ${webhook.enabled ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                          title={webhook.enabled ? 'Disabilita' : 'Abilita'}
                        >
                          {webhook.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => deleteWebhook(webhook.id)}
                          className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200"
                          title="Elimina"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="mt-3 grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Auth:</span>
                        <p className="font-medium">
                          {webhook.auth_type === 'none' && '🔓 Nessuna'}
                          {webhook.auth_type === 'api_key' && '🔑 API Key'}
                          {webhook.auth_type === 'signature' && '🔐 Signature'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Chiamate:</span>
                        <p className="font-medium">{webhook.call_count}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Ultima chiamata:</span>
                        <p className="font-medium">{formatDate(webhook.last_called_at)}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Creato:</span>
                        <p className="font-medium">{formatDate(webhook.created_at)}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Calls Tab */}
          {activeTab === 'calls' && (
            <div className="space-y-3">
              {calls.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p>Nessuna chiamata registrata</p>
                </div>
              ) : (
                calls.map(call => (
                  <div key={call.id} className="border rounded-lg p-3 flex items-center gap-4">
                    <div className={`px-2 py-1 rounded text-xs font-bold ${getStatusColor(call.status)}`}>
                      {call.status === 'success' && <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                      {call.status === 'error' && <AlertCircle className="w-3 h-3 inline mr-1" />}
                      {call.status.toUpperCase()}
                    </div>
                    <div className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">
                      {call.method}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{call.flow_name}</p>
                      <p className="text-xs text-gray-500">
                        {formatDate(call.started_at)}
                        {call.duration_ms && ` • ${call.duration_ms}ms`}
                      </p>
                    </div>
                    {call.error && (
                      <div className="text-xs text-red-500 max-w-xs truncate" title={call.error}>
                        {call.error}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Create Tab */}
          {activeTab === 'create' && (
            <div className="max-w-xl mx-auto space-y-6">
              <div className="bg-indigo-50 p-4 rounded-lg">
                <h3 className="font-bold text-indigo-800 mb-2">🔗 Nuovo Webhook</h3>
                <p className="text-sm text-indigo-700">
                  Flow corrente: <strong>{currentFlowName || 'Nessun flow selezionato'}</strong>
                </p>
              </div>

              {/* Description */}
              <div>
                <label className="block font-medium mb-2">Descrizione (opzionale)</label>
                <input
                  type="text"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Es: Webhook per ordini e-commerce"
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>

              {/* Auth Type */}
              <div>
                <label className="block font-medium mb-2">Autenticazione</label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setAuthType('none')}
                    className={`px-4 py-2 rounded-lg border-2 ${authType === 'none' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}`}
                  >
                    🔓 Nessuna
                  </button>
                  <button
                    onClick={() => setAuthType('api_key')}
                    className={`px-4 py-2 rounded-lg border-2 ${authType === 'api_key' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}`}
                  >
                    🔑 API Key
                  </button>
                  <button
                    onClick={() => setAuthType('signature')}
                    className={`px-4 py-2 rounded-lg border-2 ${authType === 'signature' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}`}
                  >
                    🔐 Signature
                  </button>
                </div>
              </div>

              {/* API Key */}
              {authType === 'api_key' && (
                <div>
                  <label className="block font-medium mb-2">API Key</label>
                  <div className="relative">
                    <input
                      type={showApiKey ? 'text' : 'password'}
                      value={apiKey}
                      onChange={e => setApiKey(e.target.value)}
                      placeholder="Genera o inserisci una API key"
                      className="w-full px-4 py-2 border rounded-lg pr-20"
                    />
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                      <button
                        onClick={() => setShowApiKey(!showApiKey)}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => setApiKey(`pk_${Math.random().toString(36).slice(2)}`)}
                        className="text-xs text-indigo-600 hover:underline"
                      >
                        Genera
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Invia come header: <code className="bg-gray-100 px-1 rounded">X-API-Key: {apiKey || 'your-key'}</code>
                  </p>
                </div>
              )}

              {/* Secret Key */}
              {authType === 'signature' && (
                <div>
                  <label className="block font-medium mb-2">Secret Key (per HMAC)</label>
                  <div className="relative">
                    <input
                      type={showApiKey ? 'text' : 'password'}
                      value={secretKey}
                      onChange={e => setSecretKey(e.target.value)}
                      placeholder="Genera o inserisci una secret key"
                      className="w-full px-4 py-2 border rounded-lg pr-20"
                    />
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                      <button
                        onClick={() => setShowApiKey(!showApiKey)}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => setSecretKey(`sk_${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`)}
                        className="text-xs text-indigo-600 hover:underline"
                      >
                        Genera
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Invia HMAC-SHA256 del body come: <code className="bg-gray-100 px-1 rounded">X-Signature: hmac-hash</code>
                  </p>
                </div>
              )}

              {/* Submit */}
              <button
                onClick={createWebhook}
                disabled={loading || !currentFlowId}
                className="w-full py-3 bg-indigo-500 text-white rounded-lg font-bold hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creazione...' : '✅ Crea Webhook'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

